import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';

const ExamCellGrievance = ({ onNavigate, userInfo }) => {
    // ====================================================================================================
    // STATE MANAGEMENT
    // ====================================================================================================
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);

    // Filters & Pagination
    const [activeTab, setActiveTab] = useState('open'); // open, pending, resolved, closed
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');

    // Modal & Actions
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Notification
    const [notification, setNotification] = useState(null);

    // ====================================================================================================
    // MOCK DATA
    // ====================================================================================================
    const categories = ['All', 'Marksheet Issue', 'Result Correction', 'Exam Form Issue', 'Admit Card Error', 'Re-evaluation', 'Other'];

    const [tickets, setTickets] = useState([
        {
            id: 'TKT-2026-101',
            studentId: 'STU045',
            studentName: 'Amit Verma',
            course: 'BCA',
            semester: '5th',
            category: 'Marksheet Issue',
            subject: 'Name incorrect on 4th Sem Marksheet',
            description: 'My name is spelled "Aamit" instead of "Amit" on the issued marksheet. I have attached my ID proof.',
            priority: 'High',
            status: 'Open',
            date: '2026-01-16T09:30:00',
            lastUpdate: '2026-01-16T09:30:00',
            attachments: ['id_proof.jpg', 'marksheet_scan.pdf'],
            thread: [
                { sender: 'Student', message: 'My name is spelled "Aamit" instead of "Amit" on the issued marksheet. I have attached my ID proof.', date: '2026-01-16T09:30:00' }
            ]
        },
        {
            id: 'TKT-2026-102',
            studentId: 'STU112',
            studentName: 'Sara Khan',
            course: 'MCA',
            semester: '3rd',
            category: 'Exam Form Issue',
            subject: 'Unable to pay exam fees',
            description: 'The payment gateway is giving an error when I try to submit the form.',
            priority: 'Medium',
            status: 'In Progress',
            date: '2026-01-15T14:15:00',
            lastUpdate: '2026-01-15T15:00:00',
            attachments: ['error_sc.png'],
            thread: [
                { sender: 'Student', message: 'The payment gateway is giving an error when I try to submit the form.', date: '2026-01-15T14:15:00' },
                { sender: 'Exam Cell', message: 'We are checking with the IT team. Please try again after 2 hours.', date: '2026-01-15T15:00:00' }
            ]
        },
        {
            id: 'TKT-2026-103',
            studentId: 'STU009',
            studentName: 'John Doe',
            course: 'MBA',
            semester: '1st',
            category: 'Admit Card Error',
            subject: 'Photo missing on Admit Card',
            description: 'My admit card has been generated but the photo is blank.',
            priority: 'High',
            status: 'Resolved',
            date: '2026-01-14T11:00:00',
            lastUpdate: '2026-01-14T16:20:00',
            attachments: [],
            thread: [
                { sender: 'Student', message: 'My admit card has been generated but the photo is blank.', date: '2026-01-14T11:00:00' },
                { sender: 'Exam Cell', message: 'Please upload your photo again in the profile section.', date: '2026-01-14T12:00:00' },
                { sender: 'Student', message: 'Done. Can you regenerate?', date: '2026-01-14T14:00:00' },
                { sender: 'Exam Cell', message: 'Regenerated. Please check now.', date: '2026-01-14T16:20:00' }
            ]
        },
        // Generating more tickets
        ...Array.from({ length: 15 }).map((_, i) => {
            const status = ['Open', 'In Progress', 'Resolved', 'Closed'][i % 4];
            return {
                id: `TKT-2026-${200 + i}`,
                studentId: `STU${500 + i}`,
                studentName: `Student ${i + 1}`,
                course: i % 2 === 0 ? 'BCA' : 'MCA',
                semester: `${(i % 6) + 1}th`,
                category: categories[(i % 5) + 1],
                subject: `Issue regarding ${categories[(i % 5) + 1]}`,
                description: 'Detailed description of the issue regarding the category selected above.',
                priority: i % 3 === 0 ? 'High' : i % 3 === 1 ? 'Medium' : 'Low',
                status: status,
                date: `2026-01-${10 + (i % 6)}T10:00:00`,
                lastUpdate: `2026-01-${10 + (i % 6)}T10:00:00`,
                attachments: [],
                thread: [{ sender: 'Student', message: 'Detailed description of the issue...', date: `2026-01-${10 + (i % 6)}T10:00:00` }]
            };
        })
    ]);

    // ====================================================================================================
    // EFFECTS & HELPERS
    // ====================================================================================================
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        setTimeout(() => setLoading(false), 800);
    }, []);

    const getPriorityColor = (p) => {
        switch (p) {
            case 'High': return '#e53e3e';
            case 'Medium': return '#dd6b20';
            case 'Low': return '#38a169';
            default: return '#718096';
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            'Open': { bg: '#fed7d7', color: '#822727', border: '#feb2b2' },
            'In Progress': { bg: '#bee3f8', color: '#2a4365', border: '#90cdf4' },
            'Resolved': { bg: '#c6f6d5', color: '#22543d', border: '#9ae6b4' },
            'Closed': { bg: '#e2e8f0', color: '#2d3748', border: '#cbd5e0' }
        };
        const s = styles[status] || styles['Closed'];
        return <span style={{ padding: '0.3rem 0.8rem', borderRadius: '20px', background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: '0.8rem', fontWeight: 'bold' }}>{status}</span>;
    };

    // ====================================================================================================
    // ACTIONS
    // ====================================================================================================
    const handleTicketClick = (ticket) => {
        setSelectedTicket(ticket);
        setIsTicketModalOpen(true);
        setReplyText('');
    };

    const handleSendReply = () => {
        if (!replyText.trim() || !selectedTicket) return;
        setIsSubmitting(true);

        setTimeout(() => {
            const updatedTickets = tickets.map(t => {
                if (t.id === selectedTicket.id) {
                    return {
                        ...t,
                        status: 'In Progress', // Auto update status on reply
                        lastUpdate: new Date().toISOString(),
                        thread: [...t.thread, { sender: 'Exam Cell', message: replyText, date: new Date().toISOString() }]
                    };
                }
                return t;
            });

            setTickets(updatedTickets);
            setSelectedTicket(updatedTickets.find(t => t.id === selectedTicket.id));
            setReplyText('');
            setIsSubmitting(false);
            setNotification({ message: 'Reply sent successfully', type: 'success' });
            setTimeout(() => setNotification(null), 3000);
        }, 800);
    };

    const handleStatusUpdate = (newStatus) => {
        if (!selectedTicket) return;
        const updatedTickets = tickets.map(t => t.id === selectedTicket.id ? { ...t, status: newStatus } : t);
        setTickets(updatedTickets);
        setSelectedTicket({ ...selectedTicket, status: newStatus });
        setNotification({ message: `Status updated to ${newStatus}`, type: 'success' });
        setTimeout(() => setNotification(null), 3000);
    };

    // ====================================================================================================
    // FILTERING
    // ====================================================================================================
    const filteredTickets = tickets.filter(t => {
        const matchesSearch =
            t.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.studentId.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.id.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCategory = filterCategory === 'All' || t.category === filterCategory;
        const matchesTab =
            activeTab === 'all' ||
            (activeTab === 'pending' && (t.status === 'Open' || t.status === 'In Progress')) ||
            (activeTab === 'resolved' && t.status === 'Resolved') ||
            (activeTab === 'closed' && t.status === 'Closed') ||
            (activeTab === 'open' && t.status === 'Open');

        return matchesSearch && matchesCategory && matchesTab;
    });

    // ====================================================================================================
    // COMPONENTS
    // ====================================================================================================

    // Ticket Details Modal
    const TicketModal = () => {
        if (!selectedTicket) return null;
        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.6)', zIndex: 1100,
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                backdropFilter: 'blur(3px)'
            }}>
                <div style={{
                    background: '#fff', width: '900px', height: '85vh', borderRadius: '16px',
                    display: 'flex', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
                    flexDirection: isMobile ? 'column' : 'row'
                }}>
                    {/* Left Details Panel */}
                    <div style={{
                        width: isMobile ? '' : '300px',
                        background: '#f8fafc',
                        borderRight: isMobile ? 'none' : '1px solid #e2e8f0',
                        borderBottom: isMobile ? '1px solid #e2e8f0' : 'none',
                        padding: '1.5rem',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h3 style={{ margin: 0, color: '#2d3748' }}>Ticket Details</h3>
                            {isMobile && <button onClick={() => setIsTicketModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem' }}>✕</button>}
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ fontSize: '0.8rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ticket ID</label>
                            <div style={{ fontWeight: '600', color: '#2d3748' }}>{selectedTicket.id}</div>
                        </div>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ fontSize: '0.8rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Student</label>
                            <div style={{ fontWeight: '600', color: '#2d3748' }}>{selectedTicket.studentName}</div>
                            <div style={{ fontSize: '0.9rem', color: '#4a5568' }}>{selectedTicket.studentId}</div>
                            <div style={{ fontSize: '0.9rem', color: '#4a5568' }}>{selectedTicket.course} - {selectedTicket.semester}</div>
                        </div>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ fontSize: '0.8rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</label>
                            <div style={{ marginTop: '0.25rem' }}>{getStatusBadge(selectedTicket.status)}</div>
                        </div>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ fontSize: '0.8rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</label>
                            <div style={{ color: '#2d3748' }}>{selectedTicket.category}</div>
                        </div>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ fontSize: '0.8rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Priority</label>
                            <div style={{ color: getPriorityColor(selectedTicket.priority), fontWeight: '600' }}>{selectedTicket.priority}</div>
                        </div>

                        <div style={{ marginTop: '2rem', borderTop: '1px solid #cbd5e0', paddingTop: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>Update Status</label>
                            <select
                                value={selectedTicket.status}
                                onChange={(e) => handleStatusUpdate(e.target.value)}
                                style={{ width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e0' }}
                            >
                                <option value="Open">Open</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Resolved">Resolved</option>
                                <option value="Closed">Closed</option>
                            </select>
                        </div>
                    </div>

                    {/* Right Conversation Panel */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        {!isMobile && (
                            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#2d3748' }}>{selectedTicket.subject}</h3>
                                <button onClick={() => setIsTicketModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' }}>✕</button>
                            </div>
                        )}

                        <div style={{ flex: 1, padding: '1.5rem', overflowY: 'auto', background: '#fff' }}>
                            {selectedTicket.thread.map((msg, idx) => (
                                <div key={idx} style={{
                                    marginBottom: '1.5rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: msg.sender === 'Exam Cell' ? 'flex-end' : 'flex-start'
                                }}>
                                    <div style={{
                                        maxWidth: '75%',
                                        padding: '1rem',
                                        borderRadius: '12px',
                                        borderTopRightRadius: msg.sender === 'Exam Cell' ? '2px' : '12px',
                                        borderTopLeftRadius: msg.sender === 'Exam Cell' ? '12px' : '2px',
                                        background: msg.sender === 'Exam Cell' ? '#238548' : '#edf2f7',
                                        color: msg.sender === 'Exam Cell' ? '#fff' : '#2d3748',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                    }}>
                                        <div style={{ fontSize: '0.95rem', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>{msg.message}</div>
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#a0aec0', marginTop: '0.25rem', padding: '0 0.5rem' }}>
                                        {msg.sender} • {new Date(msg.date).toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div style={{ padding: '1.5rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                            <textarea
                                placeholder="Type your reply here..."
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #cbd5e0', minHeight: '80px', marginBottom: '1rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ fontSize: '0.8rem', color: '#718096' }}>INTERNAL NOTES: Visible only to admins</div>
                                <button
                                    onClick={handleSendReply}
                                    disabled={!replyText.trim() || isSubmitting}
                                    style={{
                                        padding: '0.7rem 1.5rem', background: '#238548', color: '#fff', border: 'none',
                                        borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 2px 5px rgba(35, 133, 72, 0.4)'
                                    }}
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Reply'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ====================================================================================================
    // MAIN RENDER
    // ====================================================================================================
    return (
        <Layout role="examcell" onNavigate={onNavigate} userInfo={userInfo} activePage="examcell-grievance">
            <div style={{ marginBottom: '2rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.8rem', color: '#2d3748' }}>Grievance Redressal</h2>
                <p style={{ margin: '0.5rem 0 0', color: '#718096' }}>Support & Helpdesk for Examination Issues</p>
            </div>

            {notification && (
                <div style={{
                    position: 'fixed', top: '160px', right: '20px',
                    background: '#238548', color: '#fff', padding: '1rem 1.5rem',
                    borderRadius: '8px', zIndex: 2000, boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                    {notification.message}
                </div>
            )}

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', borderLeft: '4px solid #e53e3e', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ margin: 0, fontSize: '2.5rem', color: '#e53e3e' }}>{tickets.filter(t => t.priority === 'High' && t.status !== 'Closed').length}</h3>
                    <p style={{ margin: 0, color: '#718096', fontWeight: '500' }}>High Priority Open</p>
                </div>
                <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', borderLeft: '4px solid #4299e1', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ margin: 0, fontSize: '2.5rem', color: '#4299e1' }}>{tickets.filter(t => t.status === 'Open').length}</h3>
                    <p style={{ margin: 0, color: '#718096', fontWeight: '500' }}>Open Tickets</p>
                </div>
                <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', borderLeft: '4px solid #238548', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                    <h3 style={{ margin: 0, fontSize: '2.5rem', color: '#238548' }}>{tickets.filter(t => t.status === 'Resolved').length}</h3>
                    <p style={{ margin: 0, color: '#718096', fontWeight: '500' }}>Resolved This Week</p>
                </div>
            </div>

            {/* Filters */}
            <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: '250px' }}>
                        <label style={{ display: 'block', color: '#4a5568', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>Search</label>
                        <input
                            type="text" placeholder="Search ticket #, student name..."
                            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0',
                                outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#238548'}
                            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                    </div>
                    <div>
                        <label style={{ display: 'block', color: '#4a5568', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>Category</label>
                        <select
                            value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                            style={{
                                padding: '0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: '200px',
                                outline: 'none', cursor: 'pointer', boxSizing: 'border-box'
                            }}
                        >
                            {categories.map((c, i) => <option key={i} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
                    {['open', 'pending', 'resolved', 'closed', 'all'].map(t => (
                        <button
                            key={t} onClick={() => setActiveTab(t)}
                            style={{
                                padding: '0.6rem 1.2rem', borderRadius: '20px', border: '1px solid',
                                borderColor: activeTab === t ? '#238548' : '#e2e8f0',
                                background: activeTab === t ? '#f0fdf4' : 'transparent',
                                color: activeTab === t ? '#238548' : '#718096',
                                textTransform: 'capitalize', cursor: 'pointer', fontWeight: '600',
                                fontSize: '0.9rem', transition: 'all 0.2s', whiteSpace: 'nowrap'
                            }}
                        >
                            {t}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tickets List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: '#718096' }}>Loading tickets...</div>
                ) : filteredTickets.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: '#718096', background: '#fff', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>No Tickets Found</div>
                ) : (
                    filteredTickets.map(ticket => (
                        <div
                            key={ticket.id} onClick={() => handleTicketClick(ticket)}
                            style={{
                                background: '#fff', borderRadius: '12px', padding: '1.5rem',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                cursor: 'pointer', border: '1px solid transparent', transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.05)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)'; }}
                        >
                            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                                <div style={{ width: '50px', height: '50px', borderRadius: '50%', background: '#edf2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 'bold', color: '#4a5568' }}>
                                    {ticket.studentName.charAt(0)}
                                </div>
                                <div>
                                    <h4 style={{ margin: '0 0 0.25rem 0', color: '#2d3748', fontSize: '1.1rem' }}>{ticket.subject}</h4>
                                    <p style={{ margin: 0, color: '#718096', fontSize: '0.9rem' }}>
                                        <span style={{ fontWeight: '600', color: '#4a5568' }}>#{ticket.id}</span> • {ticket.studentName} • {new Date(ticket.date).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
                                {!isMobile && (
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#718096', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Priority</div>
                                        <div style={{ color: getPriorityColor(ticket.priority), fontWeight: '700' }}>{ticket.priority}</div>
                                    </div>
                                )}
                                <div style={{ textAlign: 'right', minWidth: '100px' }}>
                                    {getStatusBadge(ticket.status)}
                                </div>
                                <div style={{ color: '#cbd5e0', fontSize: '1.2rem' }}>❯</div>
                            </div>
                        </div>
                    ))
                )}
            </div>
            {isTicketModalOpen && <TicketModal />}
        </Layout>
    );
};

export default ExamCellGrievance;
